import { useState, useEffect, useMemo, useContext } from "react";
import PageContext from "../../components/Common/PageContext";
import ReceiveCardList from "../../components/ReceiveList/ReceiveCardList";
import { LoadingDialog } from "../../components/Loading/LoadingDialog";
import qs from "qs";
import { receiveStatus } from "../../util/receiveStatus";
import { getTimestampInLocalTime } from "../../util/dateUtil";
import { receiveType } from "../../util/receiveTypeMapping";
import { ModalWithActionButton } from "../../components/Modal/ModalWithActionButton";
import { useListReceives } from "./api/useListReceives";
import { useHistory, useLocation } from "react-router-dom";
import { translateError } from "../../util/axiosUtils";
import { ReceiveSummaryItem } from "../../common/ReceiveSummaryItem";
import { DisplayType } from "../../components/ReceiveList/constants";
import { getStatusDetail } from "../../util/getStatusDetail";
import useQuery from "../../useQuery";

const ReceiveListByStatus = () => {
	const history = useHistory();
	const { search } = useLocation();
	const [receiveSummaryList, setReceiveSummaryList] = useState<ReceiveSummaryItem[]>([]);
	const queryStringObject = qs.parse(search, { ignoreQueryPrefix: true });
	const { status, failureReasons, displayType } = queryStringObject;
	const [pageContext] = useContext(PageContext);
	const { userLocation } = pageContext;
	const timestamp = useQuery().get("t");
	const [{ data: listReceivesResponse, loading: isLoading, error: apiError }, refreshReceives] = useListReceives(
		status as string,
		userLocation,
		displayType as string,
		failureReasons as string,
		true
	);

	useEffect(() => {
		void refreshReceives()
	}, [refreshReceives, timestamp]);
	
	useEffect(() => {
		const { receives } = listReceivesResponse ?? {};
		if (!receives || receives.length === 0) {
			setReceiveSummaryList([]);
			return;
		}
		const receiveSummaryList = receives.map((receive) => {
			// Calculate EDI missing hours
			let inCompleteStatusHistory = receive.statusHistory?.find((s) => s.status === receiveStatus.pendingFinalise);
			let hours = 0;
			if (inCompleteStatusHistory) {
				const receiveLastUpdateTime = Date.parse(receive.timestamp);
				const inCompleteUpdateTime = Date.parse(inCompleteStatusHistory.timestamp);
				hours = parseInt(((receiveLastUpdateTime - inCompleteUpdateTime) / (1000 * 60 * 60)).toFixed(0));
			}

			return {
				id: receive.id,
				receiveType: receive.receiveType,
				purchaseOrderNumber: receive.purchaseOrderNumber,
				invoiceNumber: receive.invoiceNumber,
				store: receive.locationId,
				supplier: receive.supplierName,
				invoiceType: receive.isEdi ? "EDI" : "Manual",
				isDisabled: receive.locationId === userLocation || userLocation === 1,
				receiveStatus: receive.status,
				totalCostInc: receive.receiveLineItems.reduce((t, { lineTotalCostInc }) => t + lineTotalCostInc, 0),
				totalQty: receive.totalInvoicedQuantity,
				failureReason: receive.failureReason ?? undefined,
				hours: hours,
				latestStatusHistory: receive.statusHistory?.length > 0 ? receive.statusHistory[receive.statusHistory.length - 1] : undefined,
				dateCreated: getTimestampInLocalTime(receive.createdTimestamp),
				createdBy: `${receive.createdBy} (${receive.createdById})`,
				transfer: receive.transfer || undefined,
			};
		});
		receiveSummaryList.sort((a, b) => {
			return b.hours - a.hours;
		});
		setReceiveSummaryList(receiveSummaryList);
	}, [listReceivesResponse, userLocation]);

	const onSelectReceiveAsync = async (receiveId) => {
		try {
			const receive = receiveSummaryList.find((i) => i.id === receiveId);
			if (!receive) {
				throw new Error("Receive not found");
			}
			const { invoiceType } = receive;
			let recType = receive.receiveType ?? "";
			if (
				(recType === receiveType.edi ||
					((!recType || recType === receiveType.wasEdiLikeAManual) && invoiceType === "EDI")) &&
				receive.purchaseOrderNumber &&
				receive.invoiceNumber
			) {
				history.push(
					`/receiving?invoiceNumber=${receive.invoiceNumber}&purchaseOrderNumber=${receive.purchaseOrderNumber}&id=${receiveId}`
				);
			} else if (
				(recType === receiveType.manual ||
					((!recType || recType === receiveType.wasEdiLikeAManual) && invoiceType === "Manual")) &&
				receive.purchaseOrderNumber
			) {
				history.push(`/manualreceiving?purchaseOrderNumber=${receive.purchaseOrderNumber}&id=${receiveId}&t=${Date.now()}`);
			} else if (recType === receiveType.ediLikeAManual && receive.purchaseOrderNumber && receive.invoiceNumber) {
				history.push(
					`/edilikeamanualreceiving?invoiceNumber=${receive.invoiceNumber}&purchaseOrderNumber=${receive.purchaseOrderNumber}&id=${receiveId}&t=${Date.now()}`
				);
			} else if (!recType && invoiceType === "EDI" && receive.purchaseOrderNumber && !receive.invoiceNumber) {
				history.push(`/edilikeamanualreceiving?purchaseOrderNumber=${receive.purchaseOrderNumber}&id=${receiveId}&t=${Date.now()}`);
			}
		} catch (e) {
			console.log(e);
			history.push("/");
		}
	};

	const { message, title } = useMemo(() => {
		return getStatusDetail(status as string, displayType as DisplayType, failureReasons as string);
	}, [status, displayType, failureReasons]);

	return (
		<>
			{isLoading && <LoadingDialog message={"Retrieving Invoices..."} isOpen={isLoading} />}
			{apiError && (
				<ModalWithActionButton
					title="Error"
					alertType="error"
					primaryButtonName="Ok"
					onPrimaryButtonClick={() => history.push("/")}
					isOpen={true}
					message={translateError(apiError)}
				/>
			)}
			<ReceiveCardList receives={receiveSummaryList} title={title} message={message} onView={onSelectReceiveAsync} />
		</>
	);
};

export default ReceiveListByStatus;
